import { FC, useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import HouseImage from '@/assets/images/image-house.webp';
import useMarket from '@/api/queries/useMarket';
import {
  LoadingOverlay,
  NoDataInfo,
  PieChart,
  TooltipInfo,
} from '@/components/';
import InformationBox from './components/InformationBox';
import BrokersList from './components/BrokersList';
import { useFilterContext } from '@/context/FilterContext';
import { useMapContext } from '@/context/MapContext';
import { useProjectContext } from '@/context/ProjectContext';
import strings from '@/language/Swedish';
import { AdsMarketSharesType } from '@/types/apiTypes';
import { ButtonColor, ButtonSize, Icons, SearchParams } from '@/enums';
import Filters from '../../components/Filters';
import {
  TopRightWrapper,
  ButtonContainer,
  HideButton,
  ContentContainer,
  TableContainer,
} from '@/styles/MapAttributesStyles';
import HomeTypeTagContainer from '@/components/HomeTypeTagContainer';
import { getMarketApiParams } from '@/api/apiHelpers';
import { convertBoundsToSquareSides } from '@/helpers';
import { filterItems } from '@/pages/market/filterObjects';

const StyledMarket = styled.div`
  ${tw`flex h-full`};
`;

const Container = styled.div<{ $small?: boolean }>`
  ${tw`relative flex flex-col items-start w-full h-full mx-auto md:justify-center gap-4 md:gap-16`};

  ${({ $small }) => ($small ? tw`m-0 p-0 sm:flex-row` : tw`flex-row`)}
`;

const ContentWrapper = styled.div`
  ${tw`flex pt-4 px-6 w-full gap-6 content-between flex-col md:flex-row`};
  height: 100%;
  min-height: 0;
`;

const LeftContainer = styled.div<{ $small?: boolean }>`
  ${tw`relative flex flex-col md:gap-6`}
  width: 60%;
  max-width: 60%;

  ${({ $small }) => ($small ? tw`gap-2 w-full` : tw`gap-4`)}
`;

const ListContainer = styled.div`
  ${tw`text-xs flex flex-col`};
  width: 40%;
  max-width: 40%;
  flex: 1;
  min-height: 0;
`;

const ScrollableTableContainer = styled.div`
  overflow: scroll; 
  overflow-x: auto; 
  overflow-y: auto; 
  flex: 1; 
  height: 100%;
  width: 100%; 
  max-width: 100%; 
  
  /* Hide native scrollbars */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  
  &::-webkit-scrollbar {
    display: none; /* Webkit browsers */
  }
`;

const RightScrollbar = styled.div`
  position: absolute;
  top: 0;
  right: -2px;
  width: 10px;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  background: #f8f9fa;
  border-left: 1px solid #e9ecef;
  z-index: 20;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #4a5568;
    border-radius: 6px;
    border: 2px solid #e2e8f0;
    cursor: pointer;
  }
  &::-webkit-scrollbar-track {
    background-color: #f7fafc;
    border-radius: 6px;
  }
`;

const RightScrollbarContent = styled.div`
  width: 1px;
  pointer-events: none;
`;

const ChartWrapper = styled.div<{ $small?: boolean }>`
  ${tw`flex-1 min-h-80`}
`;

interface Props {
  small?: boolean;
}

const Market: FC<Props> = ({ small }) => {
  const { marketRectangleBounds, isMapVisible, setCoordinates } =
    useMapContext();
  const { setProject, project: {marketPolygons} } = useProjectContext();
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState<boolean>(false);
  
  // Scrollbar refs for synchronization
  const rightScrollRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rightScrollContentRef = useRef<HTMLDivElement>(null);

  const {
    marketData: marketFilterData,
    isFilterHidden,
    setIsFilterHidden,
    setMarketParam,
    resetMarketParams,
  } = useFilterContext();
  const params = getMarketApiParams(marketFilterData);
  const { data, isFetching, isError, refetch } = useMarket(
    convertBoundsToSquareSides(marketRectangleBounds),
    isFilterDialogOpen ? null : params,
    marketPolygons
  );

  useEffect(() => {
    if (data) setProject((prevState) => ({ ...prevState, marketAds: data }));
  }, [data]);

  // Sync scrollbars with table scroll position
  useEffect(() => {
    const rightScroll = rightScrollRef.current;
    const tableContainer = tableContainerRef.current;
    const rightScrollContent = rightScrollContentRef.current;

    if (!rightScroll || !tableContainer || !rightScrollContent) return;

    // Update scrollbar dimensions to match table content
    const updateScrollbarDimensions = () => {
      rightScrollContent.style.height = `${tableContainer.scrollHeight}px`;
    };

    // Sync vertical scroll positions
    const syncRightToTable = () => {
      tableContainer.scrollTop = rightScroll.scrollTop;
    };

    const syncTableToRight = () => {
      rightScroll.scrollTop = tableContainer.scrollTop;
    };

    updateScrollbarDimensions();
    
    // Add vertical scroll listeners
    rightScroll.addEventListener('scroll', syncRightToTable);
    tableContainer.addEventListener('scroll', syncTableToRight);

    // Update dimensions on content changes
    const observer = new ResizeObserver(updateScrollbarDimensions);
    observer.observe(tableContainer);

    return () => {
      rightScroll.removeEventListener('scroll', syncRightToTable);
      tableContainer.removeEventListener('scroll', syncTableToRight);
      observer.disconnect();
    };
  }, [data]);

  const sortedList = (list: AdsMarketSharesType[]) => {
    const sorted = list.sort((a, b) => b.market_share - a.market_share);
    return small ? sorted.slice(0, 10) : sorted;
  };
  return (
    <StyledMarket>
      {!small && (
        <>
          <TopRightWrapper>
            <ButtonContainer>
            <Filters
                setCoordinates={setCoordinates}
                setIsOpen={setIsFilterDialogOpen}
                handleResetFilter={() => resetMarketParams(SearchParams.filter)}
                handleClose={() => {
                  refetch(0);
                }}
                filterData={marketFilterData.filterData}
                setParam={setMarketParam}
                setMultipleParam={setMarketParam}
                filterItems={filterItems}
                isOpen={isFilterDialogOpen}
                isFilterHidden={isFilterHidden}
              />
              <TooltipInfo content={strings.dashboard.hide}>
                <div>
                  <HideButton
                    icon={Icons.arrowUp}
                    color={isFilterHidden ? ButtonColor.primary : ButtonColor.light}
                    size={ButtonSize.large}
                    $isHidden={isFilterHidden}
                    onClick={() => setIsFilterHidden((prevState) => !prevState)}
                  />
                </div>
              </TooltipInfo>
            </ButtonContainer>
            <HomeTypeTagContainer filterData={marketFilterData.filterData} />
          </TopRightWrapper>
        </>
      )}
      <ContentContainer $isFilterHidden={isFilterHidden} $isMapVisible={isMapVisible}>
        {isFetching && <LoadingOverlay />}
        {!isError && data ? (
          <TableContainer style={{ position: 'relative' }}>
            <ScrollableTableContainer ref={tableContainerRef}>
              <ContentWrapper>
                <LeftContainer>
                  <InformationBox
                    nrOfAds={data.total_ads}
                    filterData={marketFilterData.filterData}
                  />
                  <ChartWrapper $small={small}>
                    <PieChart
                      data={data.ads_market_shares}
                      chartLabel={strings.market.chartLabel}
                    />
                  </ChartWrapper>
                </LeftContainer>
                <ListContainer>
                  <BrokersList
                    listData={sortedList(data.ads_market_shares).slice(0, 20)}
                    noFilter={small}
                  />
                </ListContainer>
              </ContentWrapper>
            </ScrollableTableContainer>
            <RightScrollbar ref={rightScrollRef}>
              <RightScrollbarContent ref={rightScrollContentRef} />
            </RightScrollbar>
          </TableContainer>
        ) : (
          <NoDataInfo
            heading={
              isError
                ? strings.data.somethingWentWrong
                : strings.table.noDataHeading
            }
            altText={strings.table.noDataHeading}
            text={isError ? strings.market.error : strings.table.noDataText}
            img={HouseImage}
          />
        )}
      </ContentContainer>
    </StyledMarket>
  );
};
export default Market;
